import React from "react"
import { Button, Form, Modal } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useEmployee } from "Shared/hooks/employee.hooks"
import { useExpenseAccounts } from "Shared/hooks/expenseAccount.hooks"
import { NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { updateEmployeeRule } from "Shared/store/actions/employee/employeeActions"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { UserRule } from "@finway-group/shared/lib/models"
import { useCompany } from "Shared/hooks/company.hooks"
import { getDynamicCostCenter2List, getDynamicCostCenterList, getDynamicExpenseAccountList } from "Shared/utils/filter.utils"
import FormSelect from "Components/form/formSelect"
interface EmployeeRuleModalInterface {
    isVisible: boolean
    rule?: UserRule
    onCancel: (state: boolean) => void
}

const EmployeeRuleModal: React.FC<EmployeeRuleModalInterface> = ({ isVisible, onCancel, rule }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const initialRule = {
        costCenter: rule?.costCenter,
        costCenter2: rule?.costCenter2,
        expenseAccount: rule?.expenseAccount,
    }
    const [ruleToUpdate, setRuleToUpdate] = useState(initialRule)
    const [formInstance] = Form.useForm()
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)
    const costCenters = useSubCostCenters(true)
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()
    const employee = useEmployee()
    const { showCostCentersIds } = useCompany()

    const handleHide = () => {
        setIsLoading(false)
        onCancel(false)
    }
    const onSubmit = async () => {
        setIsLoading(true)
        try {
            const employeeId = employee && employee.id
            const rule = { ...ruleToUpdate }
            await dispatch(updateEmployeeRule(employeeId, { rule: rule }))
            handleHide()
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.updated.title"), t("notification:employee.updated.message"))
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.edit.title"))
        }
    }

    const onCostCenterChange = (costCenterParam: string) => setRuleToUpdate((rule) => ({ ...rule, costCenter: costCenterParam !== "" ? costCenterParam : undefined }))
    const onCostCenter2Change = (costCenter2Param: string) => setRuleToUpdate((rule) => ({ ...rule, costCenter2: costCenter2Param !== "" ? costCenter2Param : undefined }))
    const onExpenseAccountChange = (expenseAccountParam: string) =>
        setRuleToUpdate((rule) => ({ ...rule, expenseAccount: expenseAccountParam !== "" ? expenseAccountParam : undefined }))
    const resetFields = () => setRuleToUpdate(initialRule)

    return (
        <Modal
            destroyOnClose={true}
            afterClose={() => formInstance.resetFields()}
            visible={isVisible}
            maskClosable={false}
            title={t("label:set_employee_rule")}
            onCancel={() => {
                resetFields()
                onCancel(false)
            }}
            closable={true}
            keyboard={true}
            className="ant-modal--small"
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        resetFields()
                        onCancel(false)
                    }}
                >
                    {t("action:cancel")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isLoading}
                    onClick={() => {
                        formInstance.submit()
                    }}
                >
                    {t("action:vendor.set_rule")}
                </Button>,
            ]}
        >
            <div>
                <Form layout="vertical" form={formInstance} onFinish={onSubmit} scrollToFirstError={true}>
                    <FormSelect
                        name="costCenter"
                        header={t("input:request.cost_center")}
                        initialValue={ruleToUpdate?.costCenter}
                        options={getDynamicCostCenterList(costCenters, showCostCentersIds)}
                        fallback={String(t("missing:request.cost_center_1"))}
                        onChange={onCostCenterChange}
                        showSearch
                        isForm
                    />

                    {costCenters2.length > 0 && (
                        <FormSelect
                            name="costCenter2"
                            header={t("label:cost_center_2")}
                            initialValue={ruleToUpdate?.costCenter2}
                            options={getDynamicCostCenter2List(costCenters2)}
                            fallback={String(t("missing:request.cost_center_2"))}
                            onChange={onCostCenter2Change}
                            showSearch
                            isForm
                        />
                    )}
                    {expenseAccounts.length > 0 && (
                        <FormSelect
                            name="expenseAccounts"
                            header={t("label:expense_account")}
                            initialValue={ruleToUpdate?.expenseAccount}
                            options={getDynamicExpenseAccountList(expenseAccounts)}
                            fallback={String(t("missing:request.expense_account"))}
                            onChange={onExpenseAccountChange}
                            showSearch
                            isForm
                        />
                    )}
                </Form>
            </div>
        </Modal>
    )
}
export default EmployeeRuleModal
